import React, { useState } from 'react';
import EmployeeDashboard from '../employee/components/EmployeeDashboard';

export default function Projects() {
  const [activeView, setActiveView] = useState('showcase'); // 'showcase' or 'demo'

  const liveProjects = [
    {
      title: "Employee Management System",
      description: "A comprehensive full-stack application built with React frontend and Spring Boot backend. Features real-time employee management, advanced filtering, interactive statistics dashboard, and complete CRUD operations with a modern, responsive interface.",
      technologies: ["React", "Spring Boot", "Postgresql", "Tailwind CSS", "REST APIs", "Java", "JPA"],
      features: [
        "Complete employee lifecycle management (Create, Read, Update, Delete)",
        "Advanced filtering by department, salary range, and employment status",
        "Real-time statistics and department analytics dashboard",
        "Instant search functionality by employee name",
        "Responsive design optimized for all devices",
        "Form validation and error handling",
        "RESTful API architecture with proper HTTP methods"
      ],
      status: "live",
      image: "👥"
    }
  ];

  const professionalProjects = [
    {
      title: "5G Core Network System",
      company: "Reliance Jio",
      period: "2022-2024",
      description: "Developed a high-performance intermediary node for 5G core networks, implementing protocol translation and real-time signaling processing.",
      technologies: ["Java", "Spring Boot", "AWS ECS", "AWS lambda", "AWS S3", "SIGTRAN", "SCTP", "WebSocket"],
      achievements: [
        "Built core 5G network systems handling millions of signaling transactions",
        "Implemented protocol translation from RPC to SIGTRAN with message encoding/decoding",
        "Deployed on AWS ECS with Fargate and integrated SNS/SQS for anomaly detection",
        "Performed load testing and optimization using JConsole and Wireshark",
        "Implemented rate limiting for different message types in incoming/outgoing traffic"
      ],
      icon: "📡"
    },
    {
      title: "Credit Card Payment Gateway",
      company: "LTI",
      period: "2021-2022",
      description: "Contributed to a secure fintech payment platform supporting transaction processing, fraud detection, and real-time reconciliation.",
      technologies: ["Java 8", "Spring Boot", "Spring Cloud", "AWS lambda", "Microservices", "Kafka", "React", "JWT"],
      achievements: [
        "Developed secure payment processing with fraud detection mechanisms",
        "Implemented JWT-based authentication for API security",
        "Built Kafka-based event streams for transaction tracking",
        "Created React dashboards for merchants and customers",
        "Integrated with third-party payment networks and banking systems"
      ],
      icon: "💳"
    },
    {
      title: "Healthcare Management System",
      company: "MPHRX",
      period: "2020-2021",
      description: "Built a comprehensive healthcare platform for patient management, appointment scheduling, and medical record handling.",
      technologies: ["Java", "Spring Boot", "JPA", "MySQL", "MongoDB", "JavaScript"],
      achievements: [
        "Developed patient portal with appointment booking system",
        "Implemented secure medical record management with proper access controls",
        "Built modules for healthcare providers and administrative staff",
        "Ensured compliance with healthcare data regulations",
        "Integrated both SQL and NoSQL databases for optimal data storage"
      ],
      icon: "🏥"
    },
    {
      title: "Enterprise Logistics System",
      company: "Technoforte Software",
      period: "2019-2020",
      description: "Developed a comprehensive logistics management system for enterprise supply chain operations.",
      technologies: ["Java", "JSP", "Servlets", "JDBC", "JavaScript", "Spring", "SQL"],
      achievements: [
        "Built inventory tracking and shipment scheduling modules",
        "Developed dynamic reporting dashboards for supply chain visibility",
        "Implemented custom workflow forms for operational processes",
        "Created order management and tracking systems",
        "Enhanced system performance through code optimization"
      ],
      icon: "🚚"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-16">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Projects & Portfolio</h1>
              <p className="text-gray-600">Explore my technical work and live demonstrations</p>
            </div>

            {/* View Toggle - Only show when in demo view */}
            {activeView === 'demo' && (
              <button
                onClick={() => setActiveView('showcase')}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center space-x-2"
              >
                <span>←</span>
                <span>Back to Projects</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {activeView === 'showcase' ? (
        /* Project Showcase View */
        <div className="max-w-6xl mx-auto py-12 px-6">
          {/* Live Projects Section */}
          <section className="mb-16 w-full">
  <div className="text-center mb-8">
    <h2 className="text-2xl font-bold text-gray-900 mb-2">Live Demonstrations</h2>
    <p className="text-gray-600 max-w-md mx-auto text-sm">
      Interactive applications you can explore and test right now
    </p>
  </div>

  <div className="space-y-6 w-full">
    {liveProjects.map((project, index) => (
      <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden w-full">
        <div className="flex flex-col lg:flex-row">
          {/* Left side - Content */}
          <div className="flex-1 p-6">
            <div className="flex items-center space-x-4 mb-4">
              <span className="text-3xl">{project.image}</span>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{project.title}</h3>
                <div className="flex items-center space-x-2 mt-2">
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Live Demo
                  </span>
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
                    Full Stack
                  </span>
                </div>
              </div>
            </div>
            
            <p className="text-gray-700 mb-4">{project.description}</p>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {project.technologies.map((tech, i) => (
                <span key={i} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                  {tech}
                </span>
              ))}
            </div>
          </div>
          
          {/* Right side - Button */}
          <div className="lg:w-48 p-6 bg-gray-50 flex items-center justify-center">
            <button
              onClick={() => setActiveView('demo')}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
            >
              Launch Demo 🚀
            </button>
          </div>
        </div>
      </div>
    ))}
  </div>
</section>

          {/* Professional Projects Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Professional Projects</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Significant projects I've contributed to during my professional career
              </p>
            </div>

            <div className="space-y-8">
              {professionalProjects.map((project, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
                  <div className="p-8">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                      {/* Project Icon and Basic Info */}
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                          <span className="text-2xl text-white">{project.icon}</span>
                        </div>
                      </div>

                      {/* Project Details */}
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4">
                          <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">{project.title}</h3>
                            <div className="flex items-center space-x-4 mb-3">
                              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                {project.company}
                              </span>
                              <span className="text-gray-500 text-sm">{project.period}</span>
                            </div>
                          </div>
                        </div>

                        <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                          {project.description}
                        </p>

                        {/* Key Achievements */}
                        <div className="mb-6">
                          <h4 className="font-semibold text-gray-900 mb-3 text-lg">Key Achievements:</h4>
                          <ul className="space-y-3">
                            {project.achievements.map((achievement, i) => (
                              <li key={i} className="flex items-start space-x-3 text-gray-700">
                                <span className="text-green-500 mt-1 flex-shrink-0">•</span>
                                <span>{achievement}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Technologies */}
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3 text-lg">Technologies Used:</h4>
                          <div className="flex flex-wrap gap-2">
                            {project.technologies.map((tech, i) => (
                              <span key={i} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Call to Action */}
          <section className="text-center">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-12 text-white shadow-2xl">
              <h2 className="text-3xl font-bold mb-4">Interested in Collaboration?</h2>
              <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                Whether you need a complex backend system, a full-stack application, or technical consultation,
                I'm here to help bring your vision to life.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => setActiveView('demo')}
                  className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  🚀 Try Live Demo
                </button>
                <a
                  href="#contact"
                  className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300"
                >
                  💬 Start Conversation
                </a>
              </div>
            </div>
          </section>
        </div>
      ) : (
        /* Live Demo View */
        <div className="bg-gray-50 min-h-screen">
          {/* Demo Header */}
          {/* <div className="bg-white border-b shadow-sm">
            <div className="max-w-7xl mx-auto px-6 py-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Employee Management System</h1>
                  <p className="text-gray-600">Live Demo - Full-stack Application with React & Spring Boot</p>
                </div>
                <button
                  onClick={() => setActiveView('showcase')}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center space-x-2"
                >
                  <span>←</span>
                  <span>Back to Projects</span>
                </button>
              </div>
            </div>
          </div> */}

          {/* Employee Dashboard Component */}
          <div className="p-4 sm:p-6">
            <div className="max-w-7xl mx-auto">
              <EmployeeDashboard />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}